import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Banknote, Clock, AlertTriangle, Check, RefreshCw } from 'lucide-react';

const money = (n) => `$${(n || 0).toFixed(2)}`;
const when  = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

const STATUS_COLORS = {
  held:      'bg-amber-900/40 text-amber-200 border-amber-700/50',
  eligible:  'bg-blue-900/40 text-blue-200 border-blue-700/50',
  flagged:   'bg-orange-900/40 text-orange-200 border-orange-700/50',
  released:  'bg-emerald-900/40 text-emerald-200 border-emerald-700/50',
  cancelled: 'bg-gray-800 text-gray-300 border-gray-600',
};

const FILTERS = [
  { id: '',          label: 'All' },
  { id: 'held',      label: 'Held' },
  { id: 'eligible',  label: 'Releasing' },
  { id: 'flagged',   label: 'Needs review' },
  { id: 'released',  label: 'Paid' },
];

export default function AdminHeldPayoutsTab() {
  const [settings, setSettings] = useState(null);
  const [rows, setRows]         = useState([]);
  const [filter, setFilter]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [busy, setBusy]         = useState(null);
  const [msg, setMsg]           = useState('');
  const [vendorDays, setVendorDays] = useState({});

  const token = () => localStorage.getItem('token');

  const loadSettings = useCallback(async () => {
    const res = await axios.get(`${API}/admin/marketplace/payout-settings`, {
      headers: { Authorization: `Bearer ${token()}` },
    });
    setSettings(res.data);
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/marketplace/payouts`, {
        params: filter ? { status: filter } : {},
        headers: { Authorization: `Bearer ${token()}` },
      });
      setRows(res.data || []);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadSettings().catch(() => setSettings(null)); }, [loadSettings]);
  useEffect(() => { loadRows().catch(() => setRows([])); }, [loadRows]);

  const saveSettings = async (patch) => {
    const res = await axios.put(`${API}/admin/marketplace/payout-settings`, patch, {
      headers: { Authorization: `Bearer ${token()}` },
    });
    setSettings(res.data);
    setMsg('Payout settings saved.');
  };

  const act = async (row, action) => {
    setBusy(row.sub_order_id); setMsg('');
    try {
      if (action === 'release') {
        const res = await axios.post(`${API}/admin/marketplace/payouts/${row.sub_order_id}/release`, {}, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        setMsg(res.data.released ? `Released ${money(res.data.net)} to ${row.vendor}.` : 'Nothing left to pay out.');
      } else {
        const reason = window.prompt('Why are you stopping this payout?');
        if (!reason) { setBusy(null); return; }
        await axios.post(`${API}/admin/marketplace/payouts/${row.sub_order_id}/cancel`, { reason }, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        setMsg('Payout cancelled.');
      }
      await loadRows();
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'That did not work');
    } finally {
      setBusy(null);
    }
  };

  const runCycle = async () => {
    setBusy('cycle'); setMsg('');
    try {
      const res = await axios.post(`${API}/admin/marketplace/payouts/run-cycle`, {}, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const d = res.data;
      setMsg(`Made eligible: ${d.made_eligible} · released: ${d.released} · flagged: ${d.flagged} · unshipped nudges: ${d.unshipped_flagged}`);
      await loadRows();
    } catch (e) {
      setMsg('Could not run the payout cycle');
    } finally {
      setBusy(null);
    }
  };

  const setDays = async (vendorId) => {
    const raw = vendorDays[vendorId];
    const days = raw === '' || raw === undefined ? null : parseInt(raw, 10);
    try {
      const res = await axios.put(`${API}/admin/marketplace/vendors/${vendorId}/release-days`, { days }, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setMsg(`This vendor now releases ${res.data.effective_days} day(s) after delivery.`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Could not save that');
    }
  };

  const totals = rows.reduce((acc, r) => {
    if (r.status === 'released') acc.paid += r.net;
    else if (r.status === 'cancelled') acc.cancelled += r.net;
    else acc.holding += r.net;
    return acc;
  }, { holding: 0, paid: 0, cancelled: 0 });

  return (
    <div className="space-y-4" data-testid="admin-payouts-tab">
      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap gap-6">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-400">Holding for vendors</p>
              <p className="text-2xl text-white" data-testid="payouts-total-holding">{money(totals.holding)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-400">Paid out</p>
              <p className="text-2xl text-white">{money(totals.paid)}</p>
            </div>
            <Button size="sm" variant="outline" className="ml-auto self-end" onClick={runCycle} disabled={busy === 'cycle'} data-testid="run-payout-cycle-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Run payout cycle now
            </Button>
          </div>

          {settings && (
            <div className="flex flex-wrap gap-4 items-end pt-2 border-t border-white/10">
              {[
                ['release_days_after_delivery', 'Release days after delivery'],
                ['fallback_days_after_ship', 'Fallback days after shipping'],
                ['flag_unshipped_after_days', 'Flag unshipped after (days)'],
              ].map(([key, label]) => (
                <div key={key} className="w-52">
                  <label className="text-xs text-gray-400 block mb-1">{label}</label>
                  <Input
                    type="number"
                    defaultValue={settings[key]}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (!Number.isNaN(v) && v !== settings[key]) saveSettings({ [key]: v });
                    }}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid={`payout-setting-${key}`}
                  />
                </div>
              ))}
              <Button
                size="sm"
                variant={settings.auto_release_enabled ? 'default' : 'outline'}
                onClick={() => saveSettings({ auto_release_enabled: !settings.auto_release_enabled })}
                data-testid="toggle-auto-release"
              >
                {settings.auto_release_enabled ? <><Check className="h-4 w-4 mr-2" />Auto-release on</> : 'Auto-release off'}
              </Button>
            </div>
          )}
          {msg && <p className="text-sm text-emerald-300" data-testid="payouts-message">{msg}</p>}
        </CardContent>
      </Card>

      <div className="flex gap-2 flex-wrap">
        {FILTERS.map(f => (
          <Button
            key={f.id || 'all'}
            size="sm"
            variant={filter === f.id ? 'default' : 'outline'}
            onClick={() => setFilter(f.id)}
            data-testid={`payout-filter-${f.id || 'all'}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm">Loading payouts…</p>
      ) : rows.length === 0 ? (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="py-10 text-center text-gray-400" data-testid="admin-payouts-empty">
            <Banknote className="h-8 w-8 mx-auto mb-3 opacity-40" />
            No payouts in this view.
          </CardContent>
        </Card>
      ) : (
        rows.map(r => (
          <Card key={r.sub_order_id} className="bg-white/5 border-white/10" data-testid={`admin-payout-${r.order_number}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-sm text-white">{r.order_number}</span>
                <Badge className={`text-[10px] border ${STATUS_COLORS[r.status] || STATUS_COLORS.held}`} data-testid="admin-payout-status">
                  {r.status}
                </Badge>
                <span className="text-sm text-gray-300">{r.vendor}</span>
                <span className="text-xs text-gray-500">buyer {r.buyer} · receipt {r.receipt_status}</span>
                <span className="ml-auto text-sm text-white">{money(r.net)}</span>
              </div>
              <p className="text-xs text-gray-400 flex items-center gap-2">
                {r.status === 'flagged' && <AlertTriangle className="h-3 w-3 text-orange-300" />}
                {r.status === 'held' && <Clock className="h-3 w-3" />}
                {r.hold_reason}
                {r.eligible_at && ` · releases ${when(r.eligible_at)}`}
                {r.transfer_id && ` · ${r.transfer_id}`}
              </p>
              <p className="text-xs text-gray-500">
                Share {money(r.gross)}
                {r.label_costs > 0 && ` − labels ${money(r.label_costs)}`}
                {r.refund_debits > 0 && ` − refunds ${money(r.refund_debits)}`}
                {r.other_debits > 0 && ` − other ${money(r.other_debits)}`}
                {` = ${money(r.net)}`}
              </p>
              {!['released', 'cancelled'].includes(r.status) && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Button size="sm" disabled={busy === r.sub_order_id} onClick={() => act(r, 'release')} data-testid="admin-release-payout">
                    Release now
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy === r.sub_order_id} onClick={() => act(r, 'cancel')} data-testid="admin-cancel-payout">
                    Stop payout
                  </Button>
                  <Input
                    type="number"
                    placeholder="Release days for this vendor"
                    value={vendorDays[r.vendor_id] ?? ''}
                    onChange={(e) => setVendorDays({ ...vendorDays, [r.vendor_id]: e.target.value })}
                    className="bg-white/5 border-white/10 text-white w-56"
                    data-testid="vendor-release-days-input"
                  />
                  <Button size="sm" variant="outline" onClick={() => setDays(r.vendor_id)} data-testid="save-vendor-release-days">
                    Save vendor window
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
